// Class for handling S3 file uploads
import { baseUrl, resolutionHeader } from "../../Tenants";

class S3FileUpload {
  /**
   * Uploads a file to S3 and returns file metadata
   * @param {File} file - The file to upload
   * @param {Object} config - Configuration object containing bucketName, dirName (optional), url
   * @param {string} newFileName - The name to give the file in S3
   * @returns {Promise<Object>} - Object containing bucket, key, location, and result
   */
  static async uploadFile(file, config, newFileName) {
    try {
      // Get auth token from localStorage.
      const authToken = localStorage.getItem("auth");

      if (!authToken) {
        throw new Error("Authentication token not found");
      }

      // Regex to identify non-ASCII characters that could cause issues with S3.
      const nonAsciiCharsRegex = /[^\x00-\x7F]/g;

      // We need to properly sanatize the filename in order to remove problematic characters for S3.
      const sanitizedFileName = newFileName.replace(nonAsciiCharsRegex);

      // Create form data.
      const formData = new FormData();
      formData.append("file", file);

      // Create object key with directory if provided.
      const objectKey = `${config.dirName ? config.dirName + "/" : ""}${sanitizedFileName}`;

      // Set headers.
      const headers = {
        Authorization: `Bearer ${JSON.parse(authToken)}`,
        key: objectKey,
        "Tenant-Id": resolutionHeader,
      };

      // Make request
      const response = await fetch(`${baseUrl}api/common/upload-object`, {
        method: "POST",
        headers: headers,
        body: formData,
      });

      // Handle HTTP errors.
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `S3 upload failed with status ${response.status}: ${errorText}`,
        );
      }

      // Parse response data.
      const data = await response.json();

      // Return file metadata.
      return {
        bucket: config.bucketName,
        key: objectKey,
        location: data?.url ?? config.url + objectKey,
        result: response,
      };
    } catch (error) {
      console.error("Error uploading file to S3:", error);
      throw error; // Re-throw to allow calling code to handle the error.
    }
  }
}

const { uploadFile } = S3FileUpload;
export { uploadFile };
export default S3FileUpload;
